import React from "react";
import { useSelector } from "react-redux";
import {
  LightningIcon,
  TruckIcon,
  CaretRightIcon,
  CheckCircleIcon,
} from "@phosphor-icons/react";
import { RootState } from "../../redux/reducers/index";
import CurrencyFormat from "../../utils/currencyFormat";
import { language } from "../../utils/language";
import { FreeShippingStatus } from "../../utils/shippingRules";
import {
  Card,
  Row,
  Message,
  Track,
  Fill,
  Footer,
  Strong,
  Bold,
  Header,
  LinkButton,
  CompactCard,
  HomeContainer,
  BarRow,
} from "./styles";

type Props = {
  variant: "product" | "home" | "cartStep1" | "summary";
  status: FreeShippingStatus;
  color: string;
  onKeepShopping?: () => void;
};

const FreeShippingProgress: React.FC<Props> = ({
  variant,
  status,
  color,
  onKeepShopping,
}) => {
  const homeState: HomeStateType = useSelector(
    (state: RootState) => state.home
  );
  const lang = language[homeState.default_language];

  if (!status.applies) return null;

  if (variant === "summary" || (variant === "cartStep1" && status.reached)) {
    return (
      <CompactCard $success={status.reached}>
        {status.reached ? (
          <>
            <CheckCircleIcon size={20} color="#000000" />
            <span>{lang["freeShippingReached"]}</span>
          </>
        ) : (
          <>
            <TruckIcon size={20} color="#000000" />
            <span>
              {lang["freeShippingStepsPrefix"]}{" "}
              <CurrencyFormat amount={status.minAmount} precision={0} />.
            </span>
          </>
        )}
      </CompactCard>
    );
  }

  if (status.reached) return null;

  const progressBar = (
    <Track>
      <Fill style={{ width: `${status.percent}%` }} />
    </Track>
  );

  const footer = (
    <Footer>
      <Strong>{lang["freeShippingProductFooterStrong"]}</Strong>{" "}
      {lang["freeShippingProductFooterMiddle"]}{" "}
      <Strong>
        <CurrencyFormat amount={status.minAmount} precision={0} />
      </Strong>{" "}
      {lang["freeShippingProductFooterSuffix"]}
    </Footer>
  );

  if (variant === "home") {
    return (
      <HomeContainer>
        <Card>
          <Row>
            <LightningIcon size={24} weight="regular" color="#000000" />
            <Message>
              {lang["freeShippingProductPrefix"]}{" "}
              <CurrencyFormat amount={Math.ceil(status.missing)} precision={0} />{" "}
              {lang["freeShippingProductSuffix"]}
            </Message>
          </Row>
          <BarRow>
            {progressBar}
            {footer}
          </BarRow>
        </Card>
      </HomeContainer>
    );
  }

  if (variant === "product") {
    return (
      <Card>
        <Row>
          <LightningIcon size={24} weight="regular" color="#000000" />
          <Message>
            {lang["freeShippingProductPrefix"]}{" "}
            <CurrencyFormat amount={Math.ceil(status.missing)} precision={0} />{" "}
            {lang["freeShippingProductSuffix"]}
          </Message>
        </Row>
        {progressBar}
        {footer}
      </Card>
    );
  }

  if (variant === "cartStep1") {
    return (
      <Card>
        <Header>
          <Row>
            <TruckIcon size={24} color="#000000" />
            <Message>
              {lang["freeShippingStepOnePrefix"]}{" "}
              <Bold>
                <CurrencyFormat amount={Math.ceil(status.missing)} precision={0} />
              </Bold>{" "}
              {lang["freeShippingStepOneMiddle"]}{" "}
              <Bold>{lang["freeShippingStepOneStrong"]}</Bold>
            </Message>
          </Row>
          {onKeepShopping && (
            <LinkButton type="button" onClick={onKeepShopping}>
              {lang["keepShopping"]}
              <CaretRightIcon size={16} color="#f09252" />
            </LinkButton>
          )}
        </Header>
        {progressBar}
      </Card>
    );
  }

  return null;
};

export default FreeShippingProgress;
